import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { api } from "@/lib/api";

const AuthContext = createContext(null);

// Convert any FastAPI error detail (string | array | object) into a string
// suitable for rendering in the UI without crashing React.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // user states: undefined = checking, null = unauthenticated, object = authenticated
  const [user, setUser] = useState(undefined);

  const refreshSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  // Global 401 handler: when the server says we're no longer authenticated
  // (cookie missing, expired, or password rotated), drop the user state. The
  // ProtectedRoute will then redirect to /login. Errors from public endpoints
  // (which never return 401) are unaffected.
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        if (status === 401 && !url.includes("/auth/login")) {
          setUser(null);
        }
        return Promise.reject(error);
      },
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    try {
      const { data: me } = await api.get("/auth/me");
      setUser(me || data);
      return me || data;
    } catch (e) {
      setUser(null);
      const err = new Error("Session could not be established. Please try again.");
      err.cause = e;
      throw err;
    }
  }, []);

  const retailerLogin = useCallback(async (retailerId) => {
    // Retailer sign-in is password-less in the current MVP. Uses the same
    // cookie as admin (only one session at a time), just with a `retailer`
    // role claim in the JWT.
    const { data } = await api.post("/auth/retailer-login", {
      retailer_id: retailerId,
    });
    try {
      const { data: me } = await api.get("/auth/me");
      setUser(me || data);
      return me || data;
    } catch (e) {
      setUser(null);
      const err = new Error("Session could not be established. Please try again.");
      err.cause = e;
      throw err;
    }
  }, []);

  const switchRetailer = useCallback(async (retailerId) => {
    // Admin → impersonate a retailer. Re-fetches /auth/me so the new
    // role/impersonating fields propagate to all consumers.
    await api.post("/admin/switch-retailer", { retailer_id: retailerId });
    const { data: me } = await api.get("/auth/me");
    setUser(me);
    return me;
  }, []);

  const exitImpersonation = useCallback(async () => {
    await api.post("/auth/exit-impersonation");
    const { data: me } = await api.get("/auth/me");
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Even if the server call fails, clear local state.
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading: user === undefined, login, retailerLogin, switchRetailer, exitImpersonation, logout, refreshSession }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-[60vh] flex items-center justify-center text-sm text-gray-500"
      >
        Checking session…
      </div>
    );
  }
  if (!user) {
    const redirectTo = `${location.pathname}${location.search || ""}`;
    return <Navigate to="/login" replace state={{ from: redirectTo }} />;
  }
  return children;
}
